/*
================================================================================
FILE HEADER — apps/studio-web/src/components/SettingsPage.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Settings page            | Created — Mimiq-     |
|    |            |                   |                          | style Settings page  |
|    |            |                   |                          | for Plex Studio      |
| 2  | 2026-07-02 | Claude Sonnet 4.6 | Tier-2 Ollama (Phase 3)  | Added Local AI       |
|    |            |                   |                          | section: endpoint,   |
|    |            |                   |                          | model, Test          |
|    |            |                   |                          | Connection button    |
| 3  | 2026-07-02 | Claude Sonnet 5   | Phase 6 AI Assist mode   | Added AI Assist Mode |
| 4  | 2026-11-05 | Claude Opus 5.5   | Public hosting + rename | Visible name Plex → Scriptwave; hosted at scriptwave.qagreen.com |
|    |            |                   |                          | section with enable  |
|    |            |                   |                          | toggle (zero tokens) |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx

EXPORTS COVERED
---------------
- SettingsPage — full-width Settings tab (no mirror)

IMPORTANT POINTS
----------------
1. Full-width layout — mirror is hidden when this tab is active (same as Tests/Reports).
2. Uses useSettings() hook — every change auto-saves to localStorage immediately.
3. Sections match Mimiq's Settings page structure (Profile, Project Config, Help, FAQ).
4. Screenshot toggles are the primary new feature: screenshotOnFailure and
   screenshotEveryStep. These default RunConfigSheet values on the next run.
5. "Download plex.config.json" generates a JSON config file the user can commit
   to their project repo and use with `plex run --config plex.config.json`.
6. Light theme toggle is shown but disabled — dark only for now (Phase N).
================================================================================
*/

import { useState } from 'react';
import { useSettings } from '../lib/settings.js';
import { checkOllamaReachable } from '../lib/ollama.js';
import type { OllamaCheckResult } from '../lib/ollama.js';

const PLEX_VERSION = '0.1.0';

const BROWSER_OPTIONS = [
  { id: 'chromium' as const, label: 'Chromium', icon: '🔵' },
  { id: 'firefox'  as const, label: 'Firefox',  icon: '🦊' },
  { id: 'webkit'   as const, label: 'WebKit',   icon: '🧭' },
];

const DELAY_OPTIONS = [
  { label: '0 ms',   value: 0    },
  { label: '100 ms', value: 100  },
  { label: '300 ms', value: 300  },
  { label: '500 ms', value: 500  },
  { label: '1 s',    value: 1000 },
];

const FAQ_ITEMS = [
  {
    q: 'What is Scriptwave?',
    a: 'Scriptwave is a zero-install web test automation tool. Record in your real Chrome browser via a lightweight extension, replay tests headlessly with Playwright, and heal broken selectors locally with an ONNX model — no cloud required.',
  },
  {
    q: 'How do I record a test?',
    a: 'Install the Scriptwave Chrome extension → open Scriptwave Studio → click Start Mirror to attach a browser tab → click Start Recording → interact with the page. Each click/type is captured as an IR step with ARIA selectors.',
  },
  {
    q: 'How does Tier-1 healing work?',
    a: 'When a selector fails at replay time, the ONNX all-MiniLM-L6-v2 model (23 MB, runs locally) re-ranks all ARIA nodes on screen by semantic similarity to the original element. No internet, no API key, < 400 ms.',
  },
  {
    q: 'What does Screenshot Every Step do?',
    a: 'When enabled, the runner captures a PNG screenshot after every step. In Studio: shown as a step-by-step flipbook in the mirror. In CLI relay mode: streamed to Studio over WebSocket. Screenshots also appear in HTML reports.',
  },
  {
    q: 'What is the Studio Relay?',
    a: 'Run `plex run <file> --studio-relay` to start a local WebSocket server. Open Scriptwave Studio → Execution → Connect to CLI Run. The mirror will show screenshots from the Playwright browser after each step — works for any browser type.',
  },
  {
    q: 'Can I run tests in Firefox or WebKit?',
    a: 'Yes via the CLI: `plex run <file> --browser firefox`. Firefox and WebKit Playwright browsers must be installed first — run `plex browsers` to check, or `npx playwright install firefox`.',
  },
];

export function SettingsPage() {
  const [settings, save] = useSettings();
  const [faqOpen, setFaqOpen] = useState<Set<number>>(new Set());
  const [savedBanner, setSavedBanner] = useState(false);
  const [ollamaCheck, setOllamaCheck] = useState<{ status: 'idle' | 'testing' | 'ok' | 'error'; result?: OllamaCheckResult }>({ status: 'idle' });

  const set = <K extends keyof typeof settings>(key: K, val: typeof settings[K]) => {
    save({ [key]: val });
    showBanner();
  };

  const showBanner = () => {
    setSavedBanner(true);
    setTimeout(() => setSavedBanner(false), 1500);
  };

  const downloadConfig = () => {
    const config = {
      projectName:  settings.projectName || undefined,
      baseUrl:      settings.baseUrl || undefined,
      browser:      settings.defaultBrowser,
      headless:     settings.defaultHeadless,
      stepTimeout:  settings.defaultStepTimeoutMs,
      stepDelay:    settings.defaultStepDelayMs,
      screenshotOnFailure: settings.screenshotOnFailure,
      screenshotEveryStep: settings.screenshotEveryStep,
      studioRelayPort:     settings.studioRelayPort,
    };
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'plex.config.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const importConfig = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const raw = JSON.parse(await file.text()) as Record<string, unknown>;
        const patch: Partial<typeof settings> = {};
        if (typeof raw.projectName === 'string')  patch.projectName  = raw.projectName;
        if (typeof raw.baseUrl === 'string')       patch.baseUrl      = raw.baseUrl;
        if (raw.browser === 'chromium' || raw.browser === 'firefox' || raw.browser === 'webkit')
          patch.defaultBrowser = raw.browser;
        if (typeof raw.headless === 'boolean')     patch.defaultHeadless           = raw.headless;
        if (typeof raw.stepTimeout === 'number')   patch.defaultStepTimeoutMs       = raw.stepTimeout;
        if (typeof raw.stepDelay === 'number')     patch.defaultStepDelayMs         = raw.stepDelay;
        if (typeof raw.screenshotOnFailure === 'boolean') patch.screenshotOnFailure = raw.screenshotOnFailure;
        if (typeof raw.screenshotEveryStep === 'boolean') patch.screenshotEveryStep = raw.screenshotEveryStep;
        if (typeof raw.studioRelayPort === 'number') patch.studioRelayPort          = raw.studioRelayPort;
        save(patch);
        showBanner();
      } catch { alert('Invalid plex.config.json'); }
    };
    input.click();
  };

  const toggleFaq = (i: number) => setFaqOpen(prev => {
    const n = new Set(prev);
    n.has(i) ? n.delete(i) : n.add(i);
    return n;
  });

  const handleTestOllama = async () => {
    setOllamaCheck({ status: 'testing' });
    const result = await checkOllamaReachable({ endpoint: settings.ollamaEndpoint, model: settings.ollamaModel });
    setOllamaCheck({ status: result.ok ? 'ok' : 'error', result });
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: '#0f0f11', color: '#e4e4e7' }}>
      {/* Saved banner */}
      {savedBanner && (
        <div style={{
          position: 'fixed', top: 12, right: 20, zIndex: 9999,
          background: '#14532d', border: '1px solid #22c55e',
          borderRadius: 8, padding: '8px 16px', fontSize: 12,
          color: '#86efac', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          animation: 'fadeIn 0.15s ease',
        }}>
          ✓ Settings saved
        </div>
      )}

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px 64px' }}>

        {/* ── PROFILE ── */}
        <SectionHeader label="PROFILE" />
        <Card>
          <SettingRow label="Version">
            <span style={{ fontSize: 13, color: '#a1a1aa' }}>Scriptwave Studio v{PLEX_VERSION}</span>
          </SettingRow>

          <SettingRow label="Theme">
            <div style={{ display: 'flex', gap: 8 }}>
              <ThemeBtn active icon="🌙" label="Dark" onClick={() => {}} />
              <ThemeBtn active={false} icon="☀️" label="Light" disabled onClick={() => {}} />
            </div>
            <span style={{ fontSize: 11, color: '#3f3f46', marginLeft: 4 }}>Light — coming soon</span>
          </SettingRow>

          <SettingRow label="Your Name">
            <input
              value={settings.userName}
              onChange={e => set('userName', e.target.value)}
              placeholder="Jane Smith"
              style={inputStyle}
            />
          </SettingRow>
        </Card>

        {/* ── PROJECT CONFIG ── */}
        <SectionHeader label="PROJECT CONFIG" badge="plex.config.json" />
        <Card>
          <p style={{ fontSize: 12, color: '#71717a', marginBottom: 16, lineHeight: 1.5 }}>
            These settings apply to all test runs in this browser session.
            They work like a project-level <code style={{ color: '#a78bfa' }}>plex.config.json</code> file —
            the CLI can also use that file directly with <code style={{ color: '#a78bfa' }}>plex run --config plex.config.json</code>.
          </p>

          <SettingRow label="Project Name">
            <input
              value={settings.projectName}
              onChange={e => set('projectName', e.target.value)}
              placeholder="My Web App Tests"
              style={inputStyle}
            />
          </SettingRow>

          <SettingRow label="Base URL">
            <input
              value={settings.baseUrl}
              onChange={e => set('baseUrl', e.target.value)}
              placeholder="https://staging.example.com"
              style={inputStyle}
            />
          </SettingRow>

          <SettingRow label="Default Browser">
            <div style={{ display: 'flex', gap: 6 }}>
              {BROWSER_OPTIONS.map(b => (
                <PillBtn
                  key={b.id}
                  active={settings.defaultBrowser === b.id}
                  onClick={() => set('defaultBrowser', b.id)}
                >
                  {b.icon} {b.label}
                </PillBtn>
              ))}
            </div>
          </SettingRow>

          <SettingRow label="Default Headless">
            <Toggle
              value={settings.defaultHeadless}
              onChange={v => set('defaultHeadless', v)}
            />
            <span style={{ fontSize: 12, color: '#71717a', marginLeft: 8 }}>
              {settings.defaultHeadless ? 'No browser window — faster, for CI' : 'Browser window visible — better for debugging'}
            </span>
          </SettingRow>

          <SettingRow label="Step Timeout">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                value={settings.defaultStepTimeoutMs}
                onChange={e => set('defaultStepTimeoutMs', Math.max(500, parseInt(e.target.value, 10) || 6000))}
                style={{ ...inputStyle, width: 90 }}
              />
              <span style={{ fontSize: 12, color: '#71717a' }}>ms</span>
            </div>
          </SettingRow>

          <SettingRow label="Delay Between Steps">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DELAY_OPTIONS.map(o => (
                <PillBtn
                  key={o.value}
                  active={settings.defaultStepDelayMs === o.value}
                  onClick={() => set('defaultStepDelayMs', o.value)}
                >
                  {o.label}
                </PillBtn>
              ))}
            </div>
          </SettingRow>

          <SettingRow label="Stop on First Failure">
            <Toggle
              value={settings.defaultStopOnFirstFailure}
              onChange={v => set('defaultStopOnFirstFailure', v)}
            />
            <span style={{ fontSize: 12, color: '#71717a', marginLeft: 8 }}>
              {settings.defaultStopOnFirstFailure
                ? 'Test stops at first failed step'
                : 'All steps run — see full failure scope'}
            </span>
          </SettingRow>

          <div style={{ display: 'flex', gap: 10, marginTop: 16, paddingTop: 16, borderTop: '1px solid #27272a' }}>
            <button onClick={downloadConfig} style={outlineBtn}>↓ Download plex.config.json</button>
            <button onClick={importConfig}  style={outlineBtn}>↑ Import plex.config.json</button>
          </div>
        </Card>

        {/* ── SCREENSHOTS ── */}
        <SectionHeader label="SCREENSHOTS" />
        <Card>
          <SettingRow label="Screenshot on Failure">
            <Toggle
              value={settings.screenshotOnFailure}
              onChange={v => set('screenshotOnFailure', v)}
            />
            <span style={{ fontSize: 12, color: '#71717a', marginLeft: 8, flex: 1 }}>
              Capture browser state when a step fails — shown in Reports and exported HTML
            </span>
          </SettingRow>

          <SettingRow label="Screenshot Every Step">
            <Toggle
              value={settings.screenshotEveryStep}
              onChange={v => set('screenshotEveryStep', v)}
            />
            <div style={{ marginLeft: 8, flex: 1 }}>
              <div style={{ fontSize: 12, color: '#71717a' }}>
                Capture a PNG screenshot after every step — shown in Reports and exported HTML
              </div>
              {settings.screenshotEveryStep && (
                <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 3 }}>
                  ⚠ adds ~0.5 s per step — disable for fast CI runs
                </div>
              )}
            </div>
          </SettingRow>
        </Card>

        {/* ── STUDIO RELAY ── */}
        <SectionHeader label="CLI RELAY" badge="plex run --studio-relay" />
        <Card>
          <p style={{ fontSize: 12, color: '#71717a', marginBottom: 14, lineHeight: 1.5 }}>
            When you run <code style={{ color: '#a78bfa' }}>plex run --studio-relay</code>, a WebSocket server
            starts on the port below. Scriptwave Studio connects to it and streams per-step
            screenshots from the Playwright browser into the mirror.
          </p>
          <SettingRow label="Relay Port">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                value={settings.studioRelayPort}
                onChange={e => set('studioRelayPort', Math.max(1024, parseInt(e.target.value, 10) || 7700))}
                style={{ ...inputStyle, width: 90 }}
              />
              <code style={{ fontSize: 11, color: '#52525b' }}>ws://localhost:{settings.studioRelayPort}</code>
            </div>
          </SettingRow>
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#0d0d10', borderRadius: 6, border: '1px solid #1c1c1f', fontSize: 12, color: '#71717a' }}>
            <strong style={{ color: '#a1a1aa' }}>Usage: </strong>
            <code style={{ color: '#a78bfa' }}>plex run tests/login.json --browser firefox --studio-relay {settings.studioRelayPort !== 7700 ? settings.studioRelayPort : ''}</code>
          </div>
        </Card>

        {/* ── AI ASSIST MODE (PHASE 6) ── */}
        <SectionHeader label="AI ASSIST MODE" badge="Zero tokens · local" />
        <Card>
          <p style={{ fontSize: 12, color: '#71717a', margin: '10px 18px 14px', lineHeight: 1.6 }}>
            Adds a third recorder mode — <strong style={{ color: '#a1a1aa' }}>✨ Assist</strong> — where you type
            steps in plain English (e.g. <em>"click the button called Sign in"</em>) instead of clicking the mirror.
            Sentences are parsed by a deterministic grammar and resolved against the live page's accessibility tree
            using exact/contains matching plus a local ONNX model (<code style={{ color: '#a78bfa' }}>all-MiniLM-L6-v2</code>) —
            <strong style={{ color: '#a1a1aa' }}> no network calls, no tokens, no external LLM required.</strong>
            Every sentence is resolved and shown for confirmation before it becomes a step.
          </p>
          <SettingRow label="Enable AI Assist">
            <Toggle value={settings.aiAssistEnabled} onChange={v => set('aiAssistEnabled', v)} />
            <span style={{ fontSize: 12, color: '#71717a', marginLeft: 8 }}>
              {settings.aiAssistEnabled
                ? '✨ Assist segment visible in the Recorder'
                : 'Disabled — only Drive and Inspect modes shown'}
            </span>
          </SettingRow>
        </Card>

        {/* ── LOCAL AI (TIER-2) ── */}
        <SectionHeader label="LOCAL AI — TIER 2" badge="Ollama · qwen2.5vl" />
        <Card>
          <p style={{ fontSize: 12, color: '#71717a', margin: '10px 18px 14px', lineHeight: 1.6 }}>
            When enabled, Scriptwave uses a local <strong style={{ color: '#a1a1aa' }}>vision language model</strong> via
            Ollama to <strong style={{ color: '#a1a1aa' }}>explain failures</strong> and{' '}
            <strong style={{ color: '#a1a1aa' }}>suggest next steps</strong> — 100% offline, zero token cost.
            Requires <code style={{ color: '#a78bfa' }}>ollama</code> running locally with
            <code style={{ color: '#a78bfa' }}> OLLAMA_ORIGINS=*</code> and
            <code style={{ color: '#a78bfa' }}> qwen2.5vl:7b</code> or <code style={{ color: '#a78bfa' }}>qwen2.5vl:3b</code> pulled.
          </p>

          <SettingRow label="Enable Tier-2 AI">
            <Toggle value={settings.tier2Enabled} onChange={v => set('tier2Enabled', v)} />
            <span style={{ fontSize: 12, color: '#71717a', marginLeft: 8 }}>
              {settings.tier2Enabled
                ? '🤖 AI Analyze + Suggest Steps buttons visible in Execution and Recorder'
                : 'Disabled — no AI calls will be made'}
            </span>
          </SettingRow>

          <SettingRow label="Ollama Endpoint">
            <input
              value={settings.ollamaEndpoint}
              onChange={e => { set('ollamaEndpoint', e.target.value); setOllamaCheck({ status: 'idle' }); }}
              placeholder="http://localhost:11434"
              style={{ ...inputStyle, width: 240 }}
            />
          </SettingRow>

          <SettingRow label="Vision Model">
            <div style={{ display: 'flex', gap: 6 }}>
              {(['qwen2.5vl:7b', 'qwen2.5vl:3b'] as const).map(m => (
                <PillBtn key={m} active={settings.ollamaModel === m} onClick={() => { set('ollamaModel', m); setOllamaCheck({ status: 'idle' }); }}>
                  {m}
                </PillBtn>
              ))}
            </div>
            <span style={{ fontSize: 11, color: '#52525b', marginLeft: 4 }}>
              {settings.ollamaModel === 'qwen2.5vl:3b' ? 'faster, less accurate' : 'slower, more accurate'}
            </span>
          </SettingRow>

          {/* Connection test */}
          <div style={{ padding: '10px 18px 14px', borderTop: '1px solid #111113' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={() => void handleTestOllama()}
                disabled={ollamaCheck.status === 'testing'}
                style={{
                  background: '#27272a', border: '1px solid #3f3f46',
                  borderRadius: 6, color: '#a1a1aa', fontSize: 12,
                  padding: '6px 14px', cursor: ollamaCheck.status === 'testing' ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                }}
              >
                {ollamaCheck.status === 'testing' ? '⟳ Testing…' : '⚡ Test Connection'}
              </button>

              {ollamaCheck.status === 'ok' && ollamaCheck.result && (
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: '#22c55e', fontWeight: 600 }}>✓ Connected</span>
                  {ollamaCheck.result.hasVisionModel
                    ? <span style={{ color: '#a1a1aa', marginLeft: 6 }}>· vision model available</span>
                    : <span style={{ color: '#f59e0b', marginLeft: 6 }}>⚠ No qwen2.5vl model found — run <code>ollama pull {settings.ollamaModel}</code></span>
                  }
                  {ollamaCheck.result.availableModels.length > 0 && (
                    <span style={{ color: '#52525b', marginLeft: 6 }}>
                      ({ollamaCheck.result.availableModels.join(', ')})
                    </span>
                  )}
                </div>
              )}
              {ollamaCheck.status === 'error' && ollamaCheck.result && (
                <span style={{ fontSize: 12, color: '#f87171' }}>
                  ✗ {ollamaCheck.result.error}
                </span>
              )}
            </div>

            {/* Quick setup guide */}
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#0d0d10', borderRadius: 6, border: '1px solid #1c1c1f', fontSize: 11, color: '#52525b', lineHeight: 1.7 }}>
              <strong style={{ color: '#a1a1aa' }}>Quick setup:</strong><br />
              <code style={{ color: '#a78bfa' }}>OLLAMA_ORIGINS=* ollama serve</code> &nbsp;—&nbsp; allow Studio to call Ollama<br />
              <code style={{ color: '#a78bfa' }}>ollama pull {settings.ollamaModel}</code> &nbsp;—&nbsp; download vision model (~5 GB for 7B)
            </div>
          </div>
        </Card>

        {/* ── HELP & COMMUNITY ── */}
        <SectionHeader label="HELP & COMMUNITY" />
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <HelpRow
            icon="📖"
            title="Architecture Docs"
            desc="Full spec: IR format, step types, AI ladder, Studio layout, phased roadmap."
            action="Open PLEX_Architecture.md"
            onClick={() => window.open('PLEX_Architecture.md', '_blank')}
          />
          <HelpRow
            icon="🐛"
            title="Report an Issue"
            desc="Found a bug or have a feature request? File it on GitHub."
            action="Open GitHub Issues"
            onClick={() => window.open('https://github.com/anthropics/claude-code/issues', '_blank')}
          />
          <HelpRow
            icon="📊"
            title="Feature Status"
            desc="See what's been built, what's next, and all current feature descriptions."
            action="Open PLEX_Status.md"
            onClick={() => window.open('PLEX_Status.md', '_blank')}
            noBorder
          />
        </Card>

        {/* ── QUICK FAQ ── */}
        <SectionHeader label="QUICK FAQ" badge="full docs in Wiki / Docs tab" />
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} style={{ borderBottom: i < FAQ_ITEMS.length - 1 ? '1px solid #1c1c1f' : 'none' }}>
              <button
                onClick={() => toggleFaq(i)}
                style={{
                  width: '100%', textAlign: 'left', padding: '14px 18px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10,
                  color: '#e4e4e7', fontSize: 13,
                }}
              >
                <span style={{ color: '#7c3aed', fontSize: 12 }}>{faqOpen.has(i) ? '▼' : '▶'}</span>
                {item.q}
              </button>
              {faqOpen.has(i) && (
                <div style={{
                  padding: '0 18px 14px 36px',
                  fontSize: 12, color: '#a1a1aa', lineHeight: 1.6,
                }}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </Card>

      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ label, badge }: { label: string; badge?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '28px 0 10px' }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#71717a', letterSpacing: '0.1em' }}>{label}</span>
      {badge && (
        <span style={{ fontSize: 10, color: '#52525b', background: '#18181b', border: '1px solid #27272a', borderRadius: 4, padding: '1px 7px' }}>
          {badge}
        </span>
      )}
      <div style={{ flex: 1, height: 1, background: '#1c1c1f' }} />
    </div>
  );
}

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: '#18181b', border: '1px solid #27272a',
      borderRadius: 10, padding: '6px 0',
      ...style,
    }}>
      {children}
    </div>
  );
}

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '12px 18px', borderBottom: '1px solid #111113',
    }}>
      <span style={{ fontSize: 13, color: '#a1a1aa', minWidth: 170, flexShrink: 0 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

function ThemeBtn({ active, icon, label, disabled, onClick }: {
  active: boolean; icon: string; label: string; disabled?: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 14px', fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer',
        background: active ? '#7c3aed' : '#27272a',
        color: active ? '#fff' : disabled ? '#3f3f46' : '#a1a1aa',
        border: 'none', borderRadius: 20,
        display: 'flex', alignItems: 'center', gap: 5,
      }}
    >
      {icon} {label}
    </button>
  );
}

function PillBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px', fontSize: 12, cursor: 'pointer',
        background: active ? '#7c3aed' : '#27272a',
        color: active ? '#fff' : '#71717a',
        border: `1px solid ${active ? '#7c3aed' : '#3f3f46'}`,
        borderRadius: 20,
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      title={value ? 'ON — click to turn off' : 'OFF — click to turn on'}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: value ? '#7c3aed' : '#27272a',
        border: `1px solid ${value ? '#7c3aed' : '#3f3f46'}`,
        cursor: 'pointer', position: 'relative', flexShrink: 0,
        transition: 'background 0.2s',
      }}
    >
      <span style={{
        position: 'absolute', top: 3,
        left: value ? 22 : 3,
        width: 16, height: 16, borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </button>
  );
}

function HelpRow({ icon, title, desc, action, onClick, noBorder }: {
  icon: string; title: string; desc: string; action: string; onClick: () => void; noBorder?: boolean;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '16px 18px',
      borderBottom: noBorder ? 'none' : '1px solid #1c1c1f',
    }}>
      <div style={{ fontSize: 22, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e4e4e7', marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#71717a' }}>{desc}</div>
      </div>
      <button
        onClick={onClick}
        style={{
          background: '#7c3aed', border: 'none', borderRadius: 8,
          padding: '7px 14px', color: '#fff', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, flexShrink: 0,
        }}
        onMouseEnter={e => { (e.currentTarget).style.background = '#6d28d9'; }}
        onMouseLeave={e => { (e.currentTarget).style.background = '#7c3aed'; }}
      >
        {action}
      </button>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: '#111113',
  border: '1px solid #27272a',
  borderRadius: 6,
  padding: '7px 12px',
  color: '#e4e4e7',
  fontSize: 13,
  outline: 'none',
  flex: 1,
};

const outlineBtn: React.CSSProperties = {
  background: 'none',
  border: '1px solid #3f3f46',
  borderRadius: 7,
  padding: '7px 14px',
  color: '#a1a1aa',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
};
